import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Banknote, Building2, LayoutGrid, Handshake, Users, Database } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const ITEMS = [
  { Icon: Banknote, title: "Capital", body: "Resources to start and grow." },
  { Icon: Building2, title: "Infrastructure", body: "The operational foundation of a real company." },
  { Icon: LayoutGrid, title: "Platform", body: "Technology, finance, legal, recruiting, go-to-market." },
  { Icon: Handshake, title: "Partnership", body: "Hands-on support from operators, not observers." },
  { Icon: Users, title: "Portfolio expertise", body: "Experts across our companies to guide and mentor you." },
  { Icon: Database, title: "Tech repository", body: "Technology built across domains, yours to leverage from day one." },
];

function Item({ Icon, title, body, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-12%" });
  return (
    <motion.div
      ref={ref}
      data-testid={`platform-card-${index + 1}`}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, ease: EASE, delay: index * 0.08 }}
      className="group flex flex-col gap-4 border-t border-white/10 pt-7 transition-colors duration-500 hover:border-gold/60"
    >
      <span className={`icon-draw ${inView ? "drawn" : ""} text-gold`}>
        <Icon size={26} strokeWidth={1} />
      </span>
      <h3 className="font-serif text-3xl tracking-tight text-cream">{title}</h3>
      <p className="text-sm leading-relaxed text-mute">{body}</p>
    </motion.div>
  );
}

export default function Platform() {
  return (
    <section
      id="platform"
      data-testid="platform-section"
      className="relative scroll-mt-24 bg-noir py-24 md:py-32 lg:py-40"
    >
      <div className="mx-auto w-full max-w-7xl px-6 md:px-12">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mb-6 text-[0.72rem] font-semibold uppercase tracking-[0.32em] text-gold"
        >
          03 &mdash; What the platform includes
        </motion.p>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.05 }}
          className="max-w-4xl font-serif leading-[1.02] tracking-tight text-cream"
          style={{ fontSize: "clamp(2.5rem, 5vw, 4.5rem)" }}
        >
          Start from experience &mdash; not from scratch.
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.12 }}
          className="mt-6 max-w-xl text-lg text-mute"
        >
          Everything a company needs to exist, already in place.
        </motion.p>

        <div className="mt-16 grid grid-cols-1 gap-x-10 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
          {ITEMS.map((it, i) => (
            <Item key={it.title} {...it} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
